import { BackButton } from "@publicPortal/components/BackButton/BackButton";
import EntityTable from "@mirage/components/entityTable/EntityTable";
import { Box, IconButton, TablePaginationBaseProps, Tooltip } from "@mui/material";
import { GridColDef, GridSortItem } from "@mui/x-data-grid";
import Board from "@publicPortal/components/board/Board";
import ResponsiveAppBar from "@publicPortal/components/responsiveAppBar/ResponsiveAppBar";
import WeeklyListsService from "@publicPortal/services/WeeklyListsService";
import { Link } from "react-router-dom";
import React, { memo, useCallback, useMemo } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { GridRenderCellParams } from "@mui/x-data-grid-premium";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel, faFileWord, faFilePdf, faFileCsv } from "@fortawesome/pro-regular-svg-icons";
import { PublicPortalTheme } from "@publicPortal/theme/MuiPublicPortal.module";
import LocalAttachmentsService from "@mirage/services/LocalAttachmentsService";
import { IEntityPageRequest } from "@shared/mirage/core/contracts/IEntityPageRequest";
import { Filter, FilterItem } from "@shared/mirage/entityTable/Filter";
import DateFormatter from "@shared/mirage/helpers/DateFormatter";
import { PublicPortalRoutingService } from "@publicPortal/services/PublicPortalRoutingService";
import { useDeviceDetect } from "@mirage/hooks/useDeviceDetect";
import useAlertService from "@mirage/hooks/useAlertService";
dayjs.extend(utc);

const weeklyListsService = new WeeklyListsService();
const pp = new PublicPortalRoutingService();
const attachmentService = new LocalAttachmentsService();

export const WeeklyListsSearchResult: React.FC = memo(() => {
    const { isMobile } = useDeviceDetect();
    const showAlert = useAlertService();
    const onPageRequest = useCallback(async (pageRequest: IEntityPageRequest) => await weeklyListsService.pageRequest(pageRequest), []);
    const filter: Filter = useMemo(() => {
        const filter = { parts: [] } as Filter;
        const filterItems: FilterItem[] = [];
        const sp = new URL(window.location.href).searchParams;
        const params = Array.from(sp);

        params.forEach(([key, value]) => {
            const lowercaseKey = key.toLowerCase();

            if (lowercaseKey === "datecreatedfrom") {
                filterItems.push({ columnName: "dateCreatedFrom", value, operator: ">" });
            } else if (lowercaseKey === "datecreatedto") {
                filterItems.push({ columnName: "dateCreatedTo", value, operator: "<" });
            }
        });

        filter.parts.push({ filterItems: filterItems });
        return filter;
    }, []);
    const orderBy: GridSortItem[] = useMemo(() => [{ field: "dateCreated", sort: "desc" }], []);

    function getColumns(): GridColDef[] {
        const dateCreatedCell = (params: GridRenderCellParams) => {
            return <Link to={getRecordDetailsUrl(params.row.dateCreated)}>{params.value}</Link>;
        };

        const downloadCell = (params: GridRenderCellParams) => {
            async function downloadFile(ext: ".docx" | ".pdf") {
                const result = await attachmentService.GetAttachments("P_WEEKLY_LIST", Number(params.id));

                if (!Array.isArray(result)) {
                    showAlert("File not found", "error");
                    return;
                }
                if (result.length === 0) {
                    showAlert("File not found", "error");
                    return;
                }
                const attId = result.find(att => att.name.includes(ext))?.id;
                if (!attId) {
                    showAlert("File not found", "error");
                    return;
                }
                const attachment = await attachmentService.DownloadAttachment(attId);
                if (!attachment) {
                    showAlert("File not found", "error");
                    return;
                }
                window.location.assign(attachment.url);
            }

            return (
                <>
                    <Tooltip title={"Word"}>
                        <IconButton onClick={() => downloadFile(".docx")}>
                            <FontAwesomeIcon icon={faFileWord} style={PublicPortalTheme.icons.sizeM} color="#1B5EBE" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title={"PDF"}>
                        <IconButton onClick={() => downloadFile(".pdf")}>
                            <FontAwesomeIcon icon={faFilePdf} style={PublicPortalTheme.icons.sizeM} color="#F40F02" />
                        </IconButton>
                    </Tooltip>
                </>
            );
        };

        return [
            { field: "name", headerName: "Name", renderCell: dateCreatedCell },
            { field: "dateCreated", headerName: "Date", type: "date" },
            { field: "download", headerName: "Download", renderCell: downloadCell, sortable: false },
        ];
    }

    function getRecordDetailsUrl(dateCreated: Date) {
        const dateCreatedFormatted = DateFormatter.formatToDate(dateCreated, "YYYY-MM-DD");

        return `${pp.PlanningApplicationsRoute()}/?weeklyListDate=${dateCreatedFormatted}`;
    }
    const overridePaginationProps = useCallback((baseProps: TablePaginationBaseProps) => ({ ...baseProps, showFirstButton: false, showLastButton: false }), []);

    return (
        <>
            <ResponsiveAppBar />
            <Board size="big" headerText="Weekly Lists">
                <BackButton />
                <Box sx={{ marginTop: "32px", height: "auto" }}>
                    <EntityTable
                        title=""
                        key="weekly-list-entityTable"
                        stateKey="weeklyLists"
                        onPageRequest={onPageRequest}
                        columns={getColumns()}
                        filter={filter}
                        hideRowsPerPage={isMobile}
                        overridePaginationProps={overridePaginationProps}
                        sortModel={orderBy}
                    />
                </Box>
            </Board>
        </>
    );
});
